import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { X, UserPlus, Search, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { getClientUsers, assignUserLicense } from '../../services/api';
import { TenantUser } from '../../types';

interface BulkLicenseAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  skuId: string;
  productName: string;
  availableLicenses: number;
  onAssigned: () => void;
}

export default function BulkLicenseAssignModal({
  isOpen,
  onClose,
  clientId,
  clientName,
  skuId,
  productName,
  availableLicenses,
  onAssigned,
}: BulkLicenseAssignModalProps) {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [assigning, setAssigning] = useState(false);
  const [assignProgress, setAssignProgress] = useState({ current: 0, total: 0 });
  const [showOverAssignWarning, setShowOverAssignWarning] = useState(false);

  // Fetch users when modal opens
  useEffect(() => {
    if (isOpen && clientId) {
      setLoading(true);
      setError(null);
      setSelectedUserIds(new Set());
      setSearchTerm('');

      getClientUsers(clientId)
        .then(response => {
          setUsers(response.users);
          setLoading(false);
        })
        .catch(err => {
          setError(err instanceof Error ? err.message : 'Failed to load users');
          setLoading(false);
        });
    }
  }, [isOpen, clientId]);

  // Filter to users WITHOUT the target license
  const eligibleUsers = useMemo(() => {
    return users.filter(user => {
      // User must be enabled
      if (!user.accountEnabled) return false;
      // User must NOT already have this license
      const hasLicense = user.assignedLicenses.some(lic => lic.skuId === skuId);
      return !hasLicense;
    });
  }, [users, skuId]);

  // Apply search filter
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return eligibleUsers;
    const term = searchTerm.toLowerCase();
    return eligibleUsers.filter(user =>
      user.displayName.toLowerCase().includes(term) ||
      user.userPrincipalName.toLowerCase().includes(term) ||
      (user.mail && user.mail.toLowerCase().includes(term))
    );
  }, [eligibleUsers, searchTerm]);

  // Check if all visible users are selected
  const allSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedUserIds.has(u.id));

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      // Deselect all visible
      setSelectedUserIds(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.delete(u.id));
        return next;
      });
    } else {
      // Select all visible
      setSelectedUserIds(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.add(u.id));
        return next;
      });
    }
  };

  const handleClose = () => {
    if (!assigning) {
      onClose();
    }
  };

  const handleAssign = async () => {
    if (selectedUserIds.size === 0) return;

    // Check for over-assignment
    if (selectedUserIds.size > availableLicenses && !showOverAssignWarning) {
      setShowOverAssignWarning(true);
      return;
    }

    setAssigning(true);
    setShowOverAssignWarning(false);
    setAssignProgress({ current: 0, total: selectedUserIds.size });

    const selectedUsers = eligibleUsers.filter(u => selectedUserIds.has(u.id));
    const results = { success: 0, failed: 0, errors: [] as string[] };

    const toastId = toast.loading(`Assigning ${productName} to ${selectedUsers.length} users...`);

    for (let i = 0; i < selectedUsers.length; i++) {
      const user = selectedUsers[i];
      setAssignProgress({ current: i + 1, total: selectedUsers.length });

      try {
        await assignUserLicense(clientId, user.id, skuId);
        results.success++;
      } catch (err) {
        results.failed++;
        const errMsg = err instanceof Error ? err.message : 'Unknown error';
        results.errors.push(`${user.displayName}: ${errMsg}`);
      }

      // Rate limiting: 200ms delay between assignments
      if (i < selectedUsers.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    toast.dismiss(toastId);

    if (results.failed === 0) {
      toast.success(`Successfully assigned ${productName} to ${results.success} user(s)`, { duration: 5000 });
    } else if (results.success > 0) {
      toast.success(`Assigned ${productName} to ${results.success} user(s)`, { duration: 4000 });
      toast.error(`Failed for ${results.failed} user(s): ${results.errors.slice(0, 3).join('; ')}${results.errors.length > 3 ? '...' : ''}`, { duration: 8000 });
    } else {
      toast.error(`Failed to assign licenses: ${results.errors[0]}`, { duration: 8000 });
    }

    setAssigning(false);
    setAssignProgress({ current: 0, total: 0 });

    if (results.success > 0) {
      onAssigned();
      onClose();
    }
  };

  const isOverAssigning = selectedUserIds.size > availableLicenses;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="p-0 gap-0 sm:max-w-2xl [&>button]:hidden" onInteractOutside={(e) => assigning && e.preventDefault()}>
        {/* Header */}
        <div className="bg-muted px-4 py-4 sm:px-6 border-b border-border rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                <UserPlus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground">
                  Bulk Assign License
                </DialogTitle>
                <DialogDescription className="sr-only">Select multiple users to assign a license to in bulk</DialogDescription>
                <p className="text-sm text-muted-foreground">{clientName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={assigning}
              className="text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4 sm:px-6">
          {/* License Info */}
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {productName}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              {availableLicenses} license{availableLicenses !== 1 ? 's' : ''} available
            </p>
          </div>

          {/* Over-assign warning dialog */}
          {showOverAssignWarning && (
            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-300 dark:border-amber-700">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Over-assignment Warning
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    You selected {selectedUserIds.size} users but only {availableLicenses} license{availableLicenses !== 1 ? 's are' : ' is'} available.
                    Assignments may fail when licenses run out.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleAssign}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700"
                    >
                      Continue Anyway
                    </button>
                    <button
                      onClick={() => setShowOverAssignWarning(false)}
                      className="px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800 rounded hover:bg-amber-200 dark:hover:bg-amber-700"
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Progress indicator */}
          {assigning && (
            <div className="mb-4 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">
                  Assigning licenses...
                </span>
                <span className="text-sm font-medium text-foreground">
                  {assignProgress.current} / {assignProgress.total}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-200"
                  style={{ width: `${(assignProgress.current / assignProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-muted-foreground">Loading users...</span>
            </div>
          )}

          {/* User list */}
          {!loading && !error && (
            <>
              {/* Search */}
              <div className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search users..."
                    className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-card text-foreground text-sm focus:ring-2 focus:ring-primary-500"
                    disabled={assigning}
                  />
                </div>
              </div>

              {/* Selection info */}
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {eligibleUsers.length} user{eligibleUsers.length !== 1 ? 's' : ''} without this license
                  {searchTerm && ` (${filteredUsers.length} shown)`}
                </span>
                <span className={`font-medium ${isOverAssigning ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                  {selectedUserIds.size} selected
                </span>
              </div>

              {/* Select all checkbox */}
              {filteredUsers.length > 0 && (
                <div className="mb-2 py-2 px-3 bg-muted rounded-lg">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={assigning}
                      className="h-4 w-4 rounded border-border text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                    />
                    <span className="ml-2 text-sm font-medium text-muted-foreground">
                      Select all ({filteredUsers.length})
                    </span>
                  </label>
                </div>
              )}

              {/* User list */}
              <div className="max-h-64 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {eligibleUsers.length === 0
                      ? 'All users already have this license'
                      : 'No users match your search'}
                  </div>
                ) : (
                  filteredUsers.map(user => (
                    <label
                      key={user.id}
                      className={`flex items-center px-3 py-2 cursor-pointer hover:bg-accent ${
                        selectedUserIds.has(user.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(user.id)}
                        onChange={() => toggleUser(user.id)}
                        disabled={assigning}
                        className="h-4 w-4 rounded border-border text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                      />
                      <div className="ml-3 min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">
                          {user.displayName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {user.userPrincipalName}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground ml-2">
                        {user.licenseCount} license{user.licenseCount !== 1 ? 's' : ''}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-muted px-4 py-3 sm:px-6 border-t border-border rounded-b-lg flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {isOverAssigning && !showOverAssignWarning && (
              <span className="text-amber-600 dark:text-amber-400 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                Selecting more than available
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={assigning}
              className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-lg hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={assigning || selectedUserIds.size === 0 || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {assigning ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-1.5" />
                  Assign to {selectedUserIds.size} User{selectedUserIds.size !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
